import React, { useCallback, useEffect, useMemo, useState } from "react";
import styles from "./status-bar.module.scss";
import classnames from "classnames";
import { Tooltip } from "@whatslly/ui-components/Tooltip";
import { useTranslation } from "react-i18next";
import { Svg } from "@whatslly/ui-components/v2/BaseElements/Svg";
import { WebScriptCommands } from "@whatslly/commons/webScriptCommands";
import type { ItemType } from "../statusBarUtils";
import { updateItemAndHandleError } from "../statusBarUtils";
import { DotsMenu } from "../DotsMenu";
import { webScriptBackend } from "@whatslly/instant-messaging/webScriptBackend";

const BarItem: React.FC<{
  setCurrentItemValue: (newValue: string) => void;
  onSelectItem: (item: ItemType) => void;
  onUnSelectItem: (item: ItemType) => void;
  index: number;
  currentItemIndex: number;
  item: ItemType;
  selectedItem: ItemType | undefined;
  allowChangeItem?: boolean;
  parentItemId: string | undefined;
  items: ItemType[];
  setSaving: (val: boolean) => void;
  parentItemType: "Opportunity" | "Lead";
}> = ({
  setCurrentItemValue,
  parentItemId,
  allowChangeItem,
  onSelectItem,
  onUnSelectItem,
  index,
  currentItemIndex,
  item,
  selectedItem,
  items,
  setSaving,
  parentItemType,
}) => {
  const { t } = useTranslation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showDoneIcon, setShowDoneIcon] = useState(false);

  const selectStatus = useCallback(async () => {
    if (!item.tvsAction || !parentItemId) {
      return;
    }
    setSaving(true);
    const prevItem = items[currentItemIndex];
    await updateItemAndHandleError(item, prevItem, parentItemId, setCurrentItemValue, parentItemType);
  }, [parentItemId, item, setCurrentItemValue, currentItemIndex, items, parentItemType]);

  const isSelectedItem = useMemo(() => item.MasterLabel === selectedItem?.MasterLabel, [item, selectedItem]);

  useEffect(() => {
    setShowDoneIcon(currentItemIndex > index && !isSelectedItem);
  }, [currentItemIndex, index, isSelectedItem]);

  return (
    <Tooltip
      onVisibilityChanged={(isVisible) => {
        setIsMenuOpen(isVisible);
        if (!isVisible) {
          onUnSelectItem(item);
        } else {
          onSelectItem(item);
        }
      }}
      className={styles.subMenuTooltip}
      trigger="click"
      disabled={currentItemIndex === index || !allowChangeItem}
      placement="bottom"
      value={
        <div onClick={selectStatus} className={styles.subMenuContainer}>
          <div className={styles.subMenuWrapper}>{item.IsClosed ? t("select_closed_stage") : t("mark_as_current_stage")}</div>
        </div>
      }
    >
      <div
        onMouseEnter={() => {
          if (allowChangeItem) {
            setShowDoneIcon(false);
          }
        }}
        onMouseLeave={() => {
          if (allowChangeItem) {
            setShowDoneIcon(currentItemIndex > index && !isSelectedItem);
          }
        }}
        className={classnames(styles.itemWrapper, {
          [styles.done]: showDoneIcon,
          [styles.itemFull]: (isSelectedItem && currentItemIndex !== index) || (currentItemIndex === index && (isSelectedItem || !selectedItem)),
          [styles.itemStroke]: currentItemIndex === index && !isSelectedItem && selectedItem,
          [styles.itemWrapperDisabled]: !allowChangeItem,
        })}
      >
        <Tooltip className={classnames({ [styles.hideTooltip]: isMenuOpen })} delay={300} placement="bottom" value={<span>{item.MasterLabel}</span>}>
          <a className={styles.itemContainer}>
            <span className={classnames(styles.itemContent)}>{showDoneIcon ? <Svg iconName="v-icon" /> : <span>{item.MasterLabel}</span>}</span>
          </a>
        </Tooltip>
      </div>
    </Tooltip>
  );
};

export const StatusBar: React.FC<{
  items?: ItemType[];
  initCurrentItemValue?: string;
  className?: string;
  allowChangeItem?: boolean;
  parentItemId: string | undefined;
  parentItemType: "Opportunity" | "Lead";
}> = ({ allowChangeItem, items, initCurrentItemValue, className, parentItemId, parentItemType }) => {
  const [selectedItem, setSelectedItem] = useState<ItemType | undefined>();
  const [isSaving, setSaving] = useState(false);
  const [currentItemValue, setCurrentItemValue] = useState(initCurrentItemValue);

  const currentBarItemIndex = useMemo(() => {
    if (!items || !Array.isArray(items) || items.length === 0 || !currentItemValue) {
      return -1;
    }
    return items.findIndex((ls) => ls.MasterLabel === currentItemValue);
  }, [currentItemValue]);

  useEffect(() => {
    const unsubscribe = webScriptBackend.on(WebScriptCommands.UPDATE_STATUS_BAR_ITEM, (data) => {
      if (!data || parentItemId !== data.parentItemId) {
        return;
      }
      setSaving(false);
      if (!data.success) {
        setCurrentItemValue(data.prevSelectedItemValue);
      }
    });
    return () => {
      unsubscribe();
    };
  }, []);

  return (
    <div className={styles.statusBarContainer}>
      <div className={classnames(styles.itemsContainer, className, { [styles.disabled]: isSaving })}>
        {items?.map((item, index) => {
          return (
            <BarItem
              key={`${index}-item`}
              items={items}
              onSelectItem={(selectedItem) => setSelectedItem(selectedItem)}
              onUnSelectItem={(item) => {
                setSelectedItem((prev) => {
                  if (prev?.MasterLabel === item.MasterLabel) {
                    return undefined;
                  }
                  return prev;
                });
              }}
              index={index}
              currentItemIndex={currentBarItemIndex}
              selectedItem={selectedItem}
              item={item}
              allowChangeItem={allowChangeItem}
              parentItemId={parentItemId}
              setCurrentItemValue={setCurrentItemValue}
              setSaving={setSaving}
              parentItemType={parentItemType}
            />
          );
        })}
      </div>
      {allowChangeItem && (
        <DotsMenu
          setCurrentItemValue={setCurrentItemValue}
          parentItemId={parentItemId}
          currentBarItemIndex={currentBarItemIndex}
          items={items}
          isSaving={isSaving}
          setSaving={setSaving}
          parentItemType={parentItemType}
        />
      )}
    </div>
  );
};
